import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Save, PlusCircle, Trash2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import TriggerHubSpotSyncService from '@/services/trigger-hubspot-sync';
import { syncProductWithHubSpotRawQuery } from '@/services/hubspot-direct-sync';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2 } from 'lucide-react';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Button
} from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';

// Helper function to convert database format to UI display format (for display purposes only, not form values)
const normalizeBillingFrequency = (frequency: string): string => {
  const frequencyMap: Record<string, string> = {
    'month': 'Mensal',
    'year': 'Anual',
    'quarter': 'Trimestral',
    'semiannual': 'Semestral',
    'one-time': 'Pagamento Único'
  };
  
  return frequencyMap[frequency] || frequency;
};

// This function is kept for compatibility but we're no longer using it since we're using DB values directly
const reverseNormalizeBillingFrequency = (frequency: string): string => {
  const frequencyMap: Record<string, string> = {
    'monthly': 'month',
    'annually': 'year',
    'quarterly': 'quarter',
    'semiannually': 'semiannual',
    'one-time': 'one-time'
  };
  
  return frequencyMap[frequency] || frequency;
};

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface Vendor {
  id: string;
  name: string;
}

interface ExternalId {
  id: string;
  entity_name: string;
  entity_id: string;
  system_name: string;
  external_id: string;
  created_at: string;
  updated_at: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  family_id: string;
  sku?: string;
  vendor_id?: string;
  vendor_subscription_name?: string;
  vendor_part_number?: string;
  pricing_model?: string;
  unit_measure?: string;
  minimum_quantity?: number;
  maximum_quantity?: number;
  billing_model?: string;
  billing_period_type?: string;
  billing_period_duration?: number;
  subscription_period_type?: string;
  subscription_period_duration?: number;
  service_type?: string;
  imported?: boolean;
  vendor?: Vendor;
  prices?: SubscriptionPlanPrice[];
}

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const EditSubscriptionPlan = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("plano");

  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [externalIds, setExternalIds] = useState<ExternalId[]>([]);
  const [loadingExternalIds, setLoadingExternalIds] = useState(false);
  
  // Informações básicas do plano
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [selectedFamily, setSelectedFamily] = useState('');
  const [serviceType, setServiceType] = useState('licensing');
  
  // Informações de preço
  const [priceAmount, setPriceAmount] = useState('');
  const [currency, setCurrency] = useState('BRL');
  const [costAmount, setCostAmount] = useState('');
  const [margin, setMargin] = useState('');
  const [pricingModel, setPricingModel] = useState('flat');
  const [unitMeasure, setUnitMeasure] = useState('');
  const [minimumQuantity, setMinimumQuantity] = useState('1');
  const [maximumQuantity, setMaximumQuantity] = useState('100');
  const [planPrices, setPlanPrices] = useState<SubscriptionPlanPrice[]>([]);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  
  // Informações de cobrança
  const [billingFrequency, setBillingFrequency] = useState('month');
  const [billingModel, setBillingModel] = useState('on creation');
  const [billingPeriodDuration, setBillingPeriodDuration] = useState('1');
  const [subscriptionPeriodType, setSubscriptionPeriodType] = useState('month');
  const [subscriptionPeriodDuration, setSubscriptionPeriodDuration] = useState('1');
  const [billingCycles, setBillingCycles] = useState('forever');
  
  // Informações de fornecedor
  const [selectedVendor, setSelectedVendor] = useState('none');
  const [vendorSubscriptionName, setVendorSubscriptionName] = useState('');
  const [vendorPartNumber, setVendorPartNumber] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [planFamilies, setPlanFamilies] = useState<SubscriptionPlanFamily[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [isImported, setIsImported] = useState(false);

  // Helper functions for margin calculation
  const parseDecimal = (value: string): number | null => {
    if (!value || value.trim() === '') return null;
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
  };

  const formatDecimal = (value: number): string => {
    return value.toFixed(2).replace('.', ',');
  };

  // Calculate margin when price or cost changes
  const calculateMargin = (price: number, cost: number): string => {
    if (cost <= 0 || price <= 0) return '';
    
    // Calculate margin as percentage: ((Price - Cost) / Price) * 100
    const marginPercentage = ((price - cost) / price) * 100;
    return formatDecimal(marginPercentage);
  };

  // Handle price amount change
  const handlePriceAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPriceAmount(value);
    
    // Auto-calculate margin if both price and cost are filled
    const price = parseDecimal(value);
    const cost = parseDecimal(costAmount);
    
    if (price && cost && price > 0 && cost > 0) {
      const calculatedMargin = calculateMargin(price, cost);
      setMargin(calculatedMargin);
    }
  };

  // Handle cost amount change
  const handleCostAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCostAmount(value);
    
    // Auto-calculate margin if both price and cost are filled
    const cost = parseDecimal(value);
    const price = parseDecimal(priceAmount);
    
    if (cost && price && cost > 0 && price > 0) {
      const calculatedMargin = calculateMargin(price, cost);
      setMargin(calculatedMargin);
    }
  };

  // Handle margin change - recalculate price based on cost and margin
  const handleMarginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMargin(value);
    
    // Auto-calculate price if both cost and margin are filled
    const marginValue = parseDecimal(value);
    const cost = parseDecimal(costAmount);
    
    if (marginValue !== null && cost && cost > 0 && marginValue >= 0 && marginValue < 100) {
      // Price = Cost / (1 - Margin/100)
      const calculatedPrice = cost / (1 - marginValue / 100);
      setPriceAmount(formatDecimal(calculatedPrice));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch plan
        const { data, error } = await supabase
          .from('subscription_plans')
          .select(`
            *,
            prices:subscription_plan_prices (*)
          `)
          .eq('id', id)
          .single();

        if (error) throw error;
        if (!data) throw new Error('Plan not found');

        setPlan(data);
        setName(data.name);
        setDescription(data.description || '');
        setSku(data.sku || '');
        setSelectedFamily(data.family_id || '');
        setServiceType(data.service_type || 'licensing');
        setSelectedVendor(data.vendor_id || 'none');
        setVendorSubscriptionName(data.vendor_subscription_name || '');
        setVendorPartNumber(data.vendor_part_number || '');
        setPricingModel(data.pricing_model || 'flat');
        setUnitMeasure(data.unit_measure || '');
        setBillingFrequency(data.billing_period_type || 'month');
        
        console.log('🔍 Dados carregados do plano:', {
          pricing_model: data.pricing_model,
          unit_measure: data.unit_measure,
          billing_period_type: data.billing_period_type,
          family_id: data.family_id,
          service_type: data.service_type
        });
        setBillingCycles(data.billing_cycles || 'forever');
        setMinimumQuantity(data.minimum_quantity?.toString() || '1');
        setMaximumQuantity(data.maximum_quantity?.toString() || '100');
        setBillingModel(data.billing_model || 'on creation');
        setBillingPeriodDuration(data.billing_period_duration?.toString() || '1');
        setSubscriptionPeriodType(data.subscription_period_type || 'month');
        setSubscriptionPeriodDuration(data.subscription_period_duration?.toString() || '1');
        setIsImported(data.imported || false);

        // Se houver preços na tabela subscription_plan_prices, use-os
        if (data.prices && data.prices.length > 0) {
          setPlanPrices(data.prices);
          // Definir o primeiro preço como selecionado por padrão
          const defaultPrice = data.prices[0];
          setSelectedPriceId(defaultPrice.id);
          setPriceAmount(defaultPrice.price_amount.toString());
          setCurrency(defaultPrice.currency);
          setCostAmount(defaultPrice.cost_amount?.toString() || '');
          setMargin(defaultPrice.margin?.toString() || '');
        }
        
        // Tentar buscar fornecedores se a tabela existir
        try {
          const { data: vendorsData } = await supabase
            .from('vendors')
            .select('*')
            .order('name');
            
          setVendors(vendorsData || []);
        } catch (vendorError) {
          console.log('Tabela de fornecedores não encontrada ou não acessível');
        }

        // Buscar famílias de planos
        try {
          const { data: familiesData } = await supabase
            .from('subscription_plan_families')
            .select('*')
            .eq('is_active', true)
            .order('name');
            
          setPlanFamilies(familiesData || []);
        } catch (familiesError) {
          console.log('Tabela de famílias de planos não encontrada ou não acessível');
        }
      } catch (error) {
        console.error('Error fetching plan:', error);
        toast({
          variant: 'destructive',
          title: 'Erro ao carregar plano',
          description: 'Não foi possível carregar o plano. Por favor, recarregue a página.',
        });
        navigate('/subscription-plans');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Fetch external IDs related to this subscription plan
  useEffect(() => {
    const fetchExternalIds = async () => {
      if (!id) return;
      
      setLoadingExternalIds(true);
      try {
        const { data, error } = await supabase
          .from('external_ids')
          .select('*')
          .eq('entity_name', 'subscription_plans')
          .eq('entity_id', id);

        if (error) throw error;
        setExternalIds(data || []);
      } catch (error) {
        console.error('Error fetching external IDs:', error);
        toast({
          variant: 'destructive',
          title: 'Erro ao carregar IDs externos',
          description: 'Não foi possível carregar os IDs externos associados a este plano.',
        });
      } finally {
        setLoadingExternalIds(false);
      }
    };

    if (activeTab === 'external_ids') {
      fetchExternalIds();
    }
  }, [id, activeTab]);

  // Função para sincronizar com HubSpot usando query específica e estratégia OPTIONS + POST
  const syncWithHubSpot = async (planId: string) => {
    try {
      console.log('🔄 Iniciando sincronização com HubSpot usando nova estratégia...', { planId });
      
      // Usar o novo serviço de sincronização direta com query específica
      const success = await syncProductWithHubSpotRawQuery(planId);
      
      if (success) {
        toast({
          title: 'Sincronização Realizada',
          description: 'O produto foi sincronizado com sucesso no HubSpot.',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Aviso de Sincronização',
          description: 'Plano atualizado, mas não foi possível sincronizar com o HubSpot. Verifique se o plano possui external_id configurado.',
        });
      }
    } catch (error) {
      console.error('❌ Erro ao sincronizar com HubSpot:', error);
      toast({
        variant: 'destructive',
        title: 'Erro na Sincronização',
        description: 'Falha ao sincronizar com o HubSpot. Tente novamente.',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plan) return;
    
    if (!selectedPriceId && planPrices.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar plano',
        description: 'Adicione pelo menos um preço ao plano.',
      });
      setActiveTab("precos");
      return;
    }

    // Validate family selection
    if (!selectedFamily) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar plano',
        description: 'Selecione uma família de planos.',
      });
      setActiveTab("plano");
      return;
    }

    // Validate unit_measure when pricing_model is 'per unit'
    if (pricingModel === 'per unit' && (!unitMeasure || unitMeasure.trim() === '')) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar plano',
        description: 'Para o modelo de precificação "Por Unidade", é obrigatório definir a unidade de medida.',
      });
      setActiveTab("plano");
      return;
    }

    try {
      // Update subscription plan
      const planUpdate = {
        name,
        description: description || null,
        family_id: selectedFamily,
        service_type: serviceType,
        sku: sku || null,
        billing_period_type: billingFrequency,
        pricing_model: pricingModel,
        unit_measure: pricingModel === 'per unit' ? (unitMeasure || 'user') : null,
        minimum_quantity: parseInt(minimumQuantity),
        maximum_quantity: parseInt(maximumQuantity),
        billing_model: billingModel,
        billing_period_duration: parseInt(billingPeriodDuration),
        subscription_period_type: subscriptionPeriodType,
        subscription_period_duration: parseInt(subscriptionPeriodDuration),
        vendor_id: selectedVendor === 'none' ? null : selectedVendor,
        vendor_subscription_name: vendorSubscriptionName || null,
        vendor_part_number: vendorPartNumber || null,
      };

      console.log('💾 Dados sendo salvos:', {
        pricing_model: pricingModel,
        unit_measure: unitMeasure,
        billing_period_type: billingFrequency,
        family_id: selectedFamily,
        service_type: serviceType
      });

      const { error: planError } = await supabase
        .from('subscription_plans')
        .update(planUpdate)
        .eq('id', plan.id);

      if (planError) throw planError;

      // Sincronizar com HubSpot após atualização bem-sucedida
      await syncWithHubSpot(plan.id);

      toast({
        title: 'Plano atualizado',
        description: 'O plano foi atualizado com sucesso. A sincronização com o HubSpot será processada em segundo plano.',
      });

      navigate(`/subscription-plans/details/${plan.id}`);
    } catch (error) {
      console.error('Error updating plan:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar plano',
        description: 'Não foi possível atualizar o plano. Por favor, tente novamente.',
      });
    }
  };

  // Ao selecionar um preço diferente
  const handlePriceSelection = (priceId: string) => {
    const selectedPrice = planPrices.find(price => price.id === priceId);
    if (selectedPrice) {
      setSelectedPriceId(priceId);
      setPriceAmount(selectedPrice.price_amount.toString());
      setCurrency(selectedPrice.currency);
      setCostAmount(selectedPrice.cost_amount?.toString() || '');
      setMargin(selectedPrice.margin?.toString() || '');
    }
  };

  // Função para atualizar um preço existente
  const handleUpdatePrice = async () => {
    if (!selectedPriceId) return;
    
      try {
        const priceData = {
        subscription_plan_id: plan?.id,
          currency,
          price_amount: parseFloat(priceAmount),
          cost_amount: costAmount ? parseFloat(costAmount) : null,
          margin: margin ? parseFloat(margin) : null,
        };

      const { error } = await supabase
            .from('subscription_plan_prices')
            .update(priceData)
        .eq('id', selectedPriceId);

      if (error) throw error;
      
      // Atualizar a lista de preços
      const updatedPrices = planPrices.map(price => 
        price.id === selectedPriceId 
          ? { ...price, ...priceData } 
          : price
      );
      setPlanPrices(updatedPrices);

      // Sincronizar com HubSpot se for preço BRL ou USD
      if (plan && (currency === 'BRL' || currency === 'USD')) {
        await syncProductWithHubSpotRawQuery(plan.id);
      }
      
      toast({
        title: 'Preço atualizado',
        description: 'O preço foi atualizado com sucesso.',
      });
    } catch (error) {
      console.error('Erro ao atualizar preço:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar preço',
        description: 'Não foi possível atualizar o preço.',
      });
    }
  };

  // Função para adicionar um novo preço
  const handleAddPrice = async () => {
    if (!plan || !priceAmount || parseFloat(priceAmount) <= 0) {
      toast({
        variant: 'destructive',
        title: 'Erro ao adicionar preço',
        description: 'Informe um preço válido.',
      });
      return;
    }
    
    try {
      const priceData = {
        subscription_plan_id: plan.id,
        currency,
        price_amount: parseFloat(priceAmount),
        cost_amount: costAmount ? parseFloat(costAmount) : null,
        margin: margin ? parseFloat(margin) : null,
      };

      const { data, error } = await supabase
        .from('subscription_plan_prices')
        .insert(priceData)
        .select()
        .single();

      if (error) throw error;
      
      // Adicionar o novo preço à lista
      setPlanPrices([...planPrices, data]);
      setSelectedPriceId(data.id);

      // Sincronizar com HubSpot se for preço BRL ou USD
      if (currency === 'BRL' || currency === 'USD') {
        await syncProductWithHubSpotRawQuery(plan.id);
      }

      toast({
        title: 'Preço adicionado',
        description: 'O preço foi adicionado com sucesso.',
      });
    } catch (error) {
      console.error('Erro ao adicionar preço:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao adicionar preço',
        description: 'Não foi possível adicionar o preço.',
      });
    }
  };

  // Função para remover um preço
  const handleRemovePrice = async (priceId: string) => {
    if (!confirm('Tem certeza que deseja remover este preço?')) return;
    
    try {
      const { error } = await supabase
        .from('subscription_plan_prices')
        .delete()
        .eq('id', priceId);

      if (error) throw error;
      
      // Remover o preço da lista
      const updatedPrices = planPrices.filter(price => price.id !== priceId);
      setPlanPrices(updatedPrices);
      
      // Se o preço removido era o selecionado, selecionar outro
      if (priceId === selectedPriceId) {
        if (updatedPrices.length > 0) {
          setSelectedPriceId(updatedPrices[0].id);
          setPriceAmount(updatedPrices[0].price_amount.toString());
          setCurrency(updatedPrices[0].currency);
          setCostAmount(updatedPrices[0].cost_amount?.toString() || '');
          setMargin(updatedPrices[0].margin?.toString() || '');
        } else {
          setSelectedPriceId(null);
          setPriceAmount('');
          setCurrency('BRL');
          setCostAmount('');
          setMargin('');
        }
      }
      
      toast({
        title: 'Preço removido',
        description: 'O preço foi removido com sucesso.',
      });
    } catch (error) {
      console.error('Erro ao remover preço:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao remover preço',
        description: 'Não foi possível remover o preço.',
      });
    }
  };

  if (loading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex justify-center items-center h-[60vh]">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Carregando dados do plano...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={() => navigate('/subscription-plans')}
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar
                </Button>
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <PlusCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">Editar Plano de Assinatura</h1>
                  <p className="text-blue-100 text-base pb-2">
                    Atualize as informações do plano
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={handleSubmit}
                size="default"
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
              >
                <Save className="h-4 w-4 mr-2" />
                Salvar Alterações
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">

        {/* Enhanced Main Content with Modern Design */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm mb-8">
          <CardContent className="p-6">
            {/* Navigation Tabs */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('plano')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'plano'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Plano
                </button>
                <button
                  onClick={() => setActiveTab('precos')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'precos'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Preços
                </button>
                <button
                  onClick={() => setActiveTab('cobranca')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'cobranca'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Cobrança
                </button>
                <button
                  onClick={() => setActiveTab('fornecedor')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'fornecedor'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Fornecedor
                </button>
                <button
                  onClick={() => setActiveTab('external_ids')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'external_ids'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  IDs Externos
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
            {/* Seção de Plano */}
            {activeTab === "plano" && (
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Informações do Plano</CardTitle>
                  <CardDescription>
                    Edite as informações básicas do plano de assinatura
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-base flex items-center gap-2">
                      Nome <Badge variant="outline" className="ml-2">Obrigatório</Badge>
                    </Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="text-base"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description" className="text-base">Descrição</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="text-base min-h-[120px]"
                      rows={4}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="family" className="text-base flex items-center gap-2">
                      Família de Planos <Badge variant="outline" className="ml-2">Obrigatório</Badge>
                    </Label>
                    <Select 
                      value={selectedFamily} 
                      onValueChange={setSelectedFamily}
                      disabled={isImported}
                      required
                    >
                      <SelectTrigger className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}>
                        <SelectValue placeholder="Selecione uma família de planos" />
                      </SelectTrigger>
                      <SelectContent>
                        {planFamilies.map((family) => (
                          <SelectItem key={family.id} value={family.id}>
                            {family.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isImported && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Este plano foi importado e a família de planos não pode ser alterada.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service_type" className="text-base flex items-center gap-2">
                      Tipo de Serviço <Badge variant="outline" className="ml-2">Obrigatório</Badge>
                    </Label>
                    <Select 
                      value={serviceType} 
                      onValueChange={setServiceType}
                      disabled={isImported}
                    >
                      <SelectTrigger className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="licensing">Licenciamento</SelectItem>
                        <SelectItem value="professional_service">Serviço Profissional</SelectItem>
                        <SelectItem value="material">Material</SelectItem>
                        <SelectItem value="program">Programa</SelectItem>
                      </SelectContent>
                    </Select>
                    {isImported && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Este plano foi importado e o tipo de serviço não pode ser alterado.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sku" className="text-base">SKU</Label>
                    <Input
                      id="sku"
                      value={sku}
                      disabled={true}
                      className="bg-muted cursor-not-allowed"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      O SKU não pode ser alterado após a criação do plano.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Seção de Preços */}
            {activeTab === "precos" && (
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Informações de Preço</CardTitle>
                  <CardDescription>
                    Configure o modelo de precificação e valores
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {planPrices.length > 0 ? (
                    <>
                      <div className="rounded-md border overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Moeda</TableHead>
                              <TableHead>Preço</TableHead>
                              <TableHead>Custo</TableHead>
                              <TableHead>Margem (%)</TableHead>
                              <TableHead className="w-[100px] text-right">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {planPrices.map((price) => (
                              <TableRow 
                                key={price.id}
                                className={price.id === selectedPriceId ? "bg-muted/40" : ""}
                                onClick={() => handlePriceSelection(price.id)}
                              >
                                <TableCell>{price.currency}</TableCell>
                                <TableCell>{price.price_amount.toFixed(2)}</TableCell>
                                <TableCell>{price.cost_amount ? price.cost_amount.toFixed(2) : "-"}</TableCell>
                                <TableCell>{price.margin ? price.margin.toFixed(2) : "-"}</TableCell>
                                <TableCell className="text-right">
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemovePrice(price.id);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      <div className="bg-muted/40 p-4 rounded-lg space-y-4">
                        <h3 className="font-extrabold">{selectedPriceId ? "Editar Preço" : "Adicionar Preço"}</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                            <Label htmlFor="currency" className="text-base">Moeda</Label>
                            <Select value={currency} onValueChange={setCurrency}>
                              <SelectTrigger className="text-base">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="BRL">Real (BRL)</SelectItem>
                                <SelectItem value="USD">Dólar (USD)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="price_amount" className="text-base">Preço</Label>
                      <Input
                        id="price_amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={priceAmount}
                        onChange={handlePriceAmountChange}
                        className="text-base"
                      />
                    </div>

                    <div className="space-y-2">
                            <Label htmlFor="cost_amount" className="text-base">Custo</Label>
                            <Input
                              id="cost_amount"
                              type="number"
                              step="0.01"
                              min="0"
                              value={costAmount}
                              onChange={handleCostAmountChange}
                              className="text-base"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="margin" className="text-base">Margem (%)</Label>
                          <Input
                            id="margin"
                            type="number"
                            step="0.01"
                            min="0"
                            value={margin}
                            onChange={handleMarginChange}
                            className="text-base"
                          />
                        </div>

                        <div className="flex justify-end space-x-2 mt-4">
                          {selectedPriceId ? (
                            <>
                              <Button 
                                variant="outline" 
                                onClick={() => {
                                  setSelectedPriceId(null);
                                  setPriceAmount('');
                                  setCurrency('BRL');
                                  setCostAmount('');
                                  setMargin('');
                                }}
                              >
                                Cancelar
                              </Button>
                              <Button onClick={handleUpdatePrice}>
                                Atualizar Preço
                              </Button>
                            </>
                          ) : (
                            <Button onClick={handleAddPrice}>
                              Adicionar Preço
                            </Button>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-muted/40 p-4 rounded-lg space-y-4">
                      <p className="text-center mb-4">Nenhum preço configurado para este plano.</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="currency" className="text-base">Moeda</Label>
                      <Select value={currency} onValueChange={setCurrency}>
                        <SelectTrigger className="text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BRL">Real (BRL)</SelectItem>
                          <SelectItem value="USD">Dólar (USD)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                        <div className="space-y-2">
                          <Label htmlFor="price_amount" className="text-base">Preço</Label>
                          <Input
                            id="price_amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={priceAmount}
                            onChange={handlePriceAmountChange}
                            className="text-base"
                          />
                  </div>

                        <div className="space-y-2">
                          <Label htmlFor="cost_amount" className="text-base">Custo</Label>
                          <Input
                            id="cost_amount"
                            type="number"
                            step="0.01"
                            min="0"
                            value={costAmount}
                            onChange={handleCostAmountChange}
                            className="text-base"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="margin" className="text-base">Margem (%)</Label>
                        <Input
                          id="margin"
                          type="number"
                          step="0.01"
                          min="0"
                          value={margin}
                          onChange={handleMarginChange}
                          className="text-base"
                        />
                      </div>

                      <div className="flex justify-end space-x-2 mt-4">
                        <Button onClick={handleAddPrice}>
                          Adicionar Preço
                        </Button>
                      </div>
                    </div>
                  )}

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="pricing_model" className="text-base flex items-center gap-2">
                      Modelo de Precificação <Badge variant="outline" className="ml-2">Obrigatório</Badge>
                    </Label>
                    <Select 
                      value={pricingModel} 
                      onValueChange={setPricingModel}
                      disabled={isImported}
                    >
                      <SelectTrigger className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="flat">Valor Fixo</SelectItem>
                        <SelectItem value="per unit">Por Unidade</SelectItem>
                      </SelectContent>
                    </Select>
                    {isImported && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Este plano foi importado e o modelo de precificação não pode ser alterado.
                      </p>
                    )}
                  </div>

                  {pricingModel === 'per unit' && (
                    <div className="bg-muted/40 p-4 rounded-lg space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="unit_measure" className="text-base">Unidade de Medida</Label>
                        <Select 
                          value={unitMeasure || 'user'} 
                          onValueChange={setUnitMeasure}
                          disabled={isImported}
                        >
                          <SelectTrigger id="unit_measure" className={isImported ? "bg-muted cursor-not-allowed" : ""}>
                            <SelectValue placeholder="Selecione a unidade de medida" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">Por usuário</SelectItem>
                            <SelectItem value="hour">Por hora</SelectItem>
                          </SelectContent>
                        </Select>
                        {isImported && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Este plano foi importado e a unidade de medida não pode ser alterada.
                          </p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="minimum_quantity" className="text-base flex items-center gap-2">
                            Quantidade Mínima <Badge variant="outline" className="ml-2">Padrão: 1</Badge>
                          </Label>
                          <Input
                            id="minimum_quantity"
                            type="number"
                            min="0"
                            value={minimumQuantity}
                            onChange={(e) => setMinimumQuantity(e.target.value)}
                            disabled={isImported}
                            className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}
                          />
                          {isImported && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Este plano foi importado e a quantidade mínima não pode ser alterada.
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="maximum_quantity" className="text-base flex items-center gap-2">
                            Quantidade Máxima <Badge variant="outline" className="ml-2">Padrão: 100</Badge>
                          </Label>
                          <Input
                            id="maximum_quantity"
                            type="number"
                            min="0"
                            value={maximumQuantity}
                            onChange={(e) => setMaximumQuantity(e.target.value)}
                            disabled={isImported}
                            className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}
                          />
                          {isImported && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Este plano foi importado e a quantidade máxima não pode ser alterada.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Seção de Cobrança */}
            {activeTab === "cobranca" && (
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Informações de Cobrança</CardTitle>
                  <CardDescription>
                    Configure o modelo e periodicidade de cobrança
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="billing_model" className="text-base flex items-center gap-2">
                      Modelo de Cobrança <Badge variant="outline" className="ml-2">Padrão: Na criação</Badge>
                    </Label>
                    <Select 
                      value={billingModel} 
                      onValueChange={setBillingModel}
                      disabled={isImported}
                    >
                      <SelectTrigger className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on creation">Na criação</SelectItem>
                        <SelectItem value="on activation">Na ativação</SelectItem>
                        <SelectItem value="on usage">No uso</SelectItem>
                      </SelectContent>
                    </Select>
                    {isImported && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Este plano foi importado e o modelo de cobrança não pode ser alterado.
                      </p>
                    )}
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="billing_frequency" className="text-base flex items-center gap-2">
                        Período de Cobrança <Badge variant="outline" className="ml-2">Padrão: Mensal</Badge>
                      </Label>
                      <Select 
                        value={billingFrequency} 
                        onValueChange={setBillingFrequency}
                        disabled={isImported}
                      >
                        <SelectTrigger className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="month">Mensal</SelectItem>
                          <SelectItem value="year">Anual</SelectItem>
                          <SelectItem value="one-time">Pagamento Único</SelectItem>
                        </SelectContent>
                      </Select>
                      {isImported && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Este plano foi importado e o período de cobrança não pode ser alterado.
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="billing_period_duration" className="text-base">
                        Duração do Período de Cobrança
                      </Label>
                      <Input
                        id="billing_period_duration"
                        type="number"
                        min="1"
                        value={billingPeriodDuration}
                        onChange={(e) => setBillingPeriodDuration(e.target.value)}
                        placeholder={`Número de ${billingFrequency === 'month' ? 'meses' : billingFrequency === 'year' ? 'anos' : 'pagamentos'}`}
                        className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}
                        disabled={isImported}
                      />
                      {isImported && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Este plano foi importado e a duração do período de cobrança não pode ser alterada.
                        </p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="subscription_period_type" className="text-base flex items-center gap-2">
                        Período de Assinatura <Badge variant="outline" className="ml-2">Padrão: Mês</Badge>
                      </Label>
                      <Select 
                        value={subscriptionPeriodType} 
                        onValueChange={setSubscriptionPeriodType}
                        disabled={isImported}
                      >
                        <SelectTrigger className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="month">Mensal</SelectItem>
                          <SelectItem value="year">Anual</SelectItem>
                          <SelectItem value="one-time">Pagamento Único</SelectItem>
                        </SelectContent>
                      </Select>
                      {isImported && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Este plano foi importado e o período de assinatura não pode ser alterado.
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subscription_period_duration" className="text-base">
                        Duração do Período de Assinatura
                      </Label>
                      <Input
                        id="subscription_period_duration"
                        type="number"
                        min="1"
                        value={subscriptionPeriodDuration}
                        onChange={(e) => setSubscriptionPeriodDuration(e.target.value)}
                        placeholder={`Número de ${subscriptionPeriodType === 'month' ? 'meses' : 'anos'}`}
                        className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}
                        disabled={isImported}
                      />
                      {isImported && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Este plano foi importado e a duração do período de assinatura não pode ser alterada.
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Seção de Fornecedor */}
            {activeTab === "fornecedor" && (
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Informações do Fornecedor</CardTitle>
                  <CardDescription>
                    Associe este plano a um fornecedor (opcional)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="vendor" className="text-base">Fornecedor</Label>
                    <Select 
                      value={selectedVendor} 
                      onValueChange={setSelectedVendor}
                      disabled={isImported}
                    >
                      <SelectTrigger className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum</SelectItem>
                        {vendors.map((vendor) => (
                          <SelectItem key={vendor.id} value={vendor.id}>
                            {vendor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isImported && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Este plano foi importado e o fornecedor não pode ser alterado.
                      </p>
                    )}
                  </div>

                  {selectedVendor !== 'none' && (
                    <div className="bg-muted/40 p-4 rounded-lg space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="vendor_subscription_name" className="text-base">
                          Nome da Assinatura no Fornecedor
                        </Label>
                        <Input
                          id="vendor_subscription_name"
                          value={vendorSubscriptionName}
                          onChange={(e) => setVendorSubscriptionName(e.target.value)}
                          className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}
                          disabled={isImported}
                        />
                        {isImported && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Este plano foi importado e o nome da assinatura no fornecedor não pode ser alterado.
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="vendor_part_number" className="text-base">
                          Número da Parte no Fornecedor
                        </Label>
                        <Input
                          id="vendor_part_number"
                          value={vendorPartNumber}
                          onChange={(e) => setVendorPartNumber(e.target.value)}
                          className={`text-base ${isImported ? "bg-muted cursor-not-allowed" : ""}`}
                          disabled={isImported}
                        />
                        {isImported && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Este plano foi importado e o número da parte no fornecedor não pode ser alterado.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* External IDs Tab */}
            {activeTab === "external_ids" && (
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>IDs Externos</CardTitle>
                  <CardDescription>
                    IDs externos associados a este plano de assinatura em sistemas integrados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingExternalIds ? (
                    <div className="flex justify-center items-center h-32">
                      <p className="text-muted-foreground">Carregando IDs externos...</p>
                    </div>
                  ) : externalIds.length > 0 ? (
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Sistema</TableHead>
                            <TableHead>ID Externo</TableHead>
                            <TableHead>Data de Criação</TableHead>
                            <TableHead>Última Atualização</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {externalIds.map((extId) => (
                            <TableRow key={extId.id}>
                              <TableCell className="font-extrabold">{extId.system_name}</TableCell>
                              <TableCell>{extId.external_id}</TableCell>
                              <TableCell>{new Date(extId.created_at).toLocaleString()}</TableCell>
                              <TableCell>{new Date(extId.updated_at).toLocaleString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="bg-muted/40 rounded-lg p-8 text-center">
                      <p className="text-muted-foreground mb-4">Nenhum ID externo associado a este plano.</p>
                      <p className="text-sm text-muted-foreground">
                        IDs externos são criados automaticamente quando este plano é integrado com sistemas externos.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            </form>
          </CardContent>
        </Card>

        {/* Bottom Spacing */}
        <div className="h-16" />
      </div>
    </div>
  );
};

export default EditSubscriptionPlan; 